import { call, put } from 'redux-saga/effects';
import * as types from './node.types';
import * as actions from './node.actions';
import { select, selectWithParams } from '../../../utils/selector.utils';
import { nodeSelectors } from './node.selectors';
import { apiRequest } from '../../api/api.saga-services';
import * as api from '../../api/request-config/chart.api';

function parentIdsFromStoreInput(node: types.ParentRef): string[] {
  if (node.parentIds && node.parentIds.length > 0) {
    return node.parentIds.map(String);
  }
  if (Array.isArray(node.parentId)) {
    return node.parentId.map(String);
  }
  if (node.parentId !== undefined && node.parentId !== null && String(node.parentId) !== '') {
    return [String(node.parentId)];
  }
  return [];
}

function withNormalizedParents(node): types.IOrganizationChartNode {
  const parentIds = parentIdsFromStoreInput(node);
  const userId =
    node.userId !== undefined && node.userId !== null && String(node.userId) !== '' ? String(node.userId) : '';
  let positionX = Number(node.positionX);
  let positionY = Number(node.positionY);
  if (!Number.isFinite(positionX) || !Number.isFinite(positionY)) {
    positionX = 0;
    positionY = 0;
  }

  return {
    id: String(node.id),
    chartId: String(node.chartId),
    parentIds,
    userId,
    description: String(node.description ?? ''),
    color: String(node.color ?? ''),
    changedBy: String(node.changedBy ?? ''),
    changedAt: String(node.changedAt ?? ''),
    nodeTitle: String(node.nodeTitle ?? ''),
    externalUser: node.externalUser ?? null,
    positionX,
    positionY,
    children: node.children,
  };
}

export function* storeNode(nodes) {
  const arr = Array.isArray(nodes) ? nodes : [nodes];
  const normalized = arr.map(withNormalizedParents);
  yield put(actions.storeNode(normalized));

  return arr.length === 1 ? normalized[0] : normalized;
}

export function* clearNode(nodeId: types.IOrganizationChartNode['id']) {
  yield put(actions.clearNode(nodeId));
}

export function* clearAllNodes() {
  const nodeIds: types.IOrganizationChartNode['id'][] = yield select(nodeSelectors.selectResourceIds);
  yield put(actions.clearNode(nodeIds));
}

export function* createNode(data: types.INodeCreate, chartType: string) {
  const result = (yield call(apiRequest, api.createNodeApi(data, chartType))) as types.IChartNodeSaveResult | null;
  if (!result?.node) {
    return;
  }
  const node = (yield call(storeNode, result.node)) as types.IOrganizationChartNode;
  return { node, chart: result.chart };
}

export function* updateNode(nodeId: types.IOrganizationChartNode['id'], data: types.INodeUpdate) {
  const result = (yield call(apiRequest, api.updateNodeApi(nodeId, data))) as types.IChartNodeSaveResult | null;
  if (!result?.node) {
    return;
  }
  yield call(clearNode, result.node.id);
  const node = (yield call(storeNode, result.node)) as types.IOrganizationChartNode;
  return { node, chart: result.chart };
}

export function* updateNodesPositions(chartType: string, nodes: types.INodePositionUpdate[]) {
  if (!nodes.length) {
    return;
  }

  const nodeIds = nodes.map((n) => n.id);
  const existingNodes = (yield selectWithParams(
    nodeSelectors.selectResourcesById,
    nodeIds,
  )) as (types.IOrganizationChartNode | null)[];

  const existingById = new Map<string, types.IOrganizationChartNode>(
    nodeIds.flatMap((id, i): [string, types.IOrganizationChartNode][] => {
      const node = existingNodes[i];
      return node ? [[String(id), node]] : [];
    }),
  );

  const result = (yield call(
    apiRequest,
    api.updateNodesPositionsApi(chartType, { nodes }),
  )) as types.IChartNodePositionsResult | null;
  if (!result?.nodes?.length) {
    return;
  }

  const merged: types.IOrganizationChartNode[] = [];
  for (const update of result.nodes) {
    const existing = existingById.get(String(update.id));
    if (existing) {
      merged.push({
        ...existing,
        positionX: update.positionX,
        positionY: update.positionY,
      });
    }
  }
  if (!merged.length) {
    return;
  }
  const stored = (yield call(storeNode, merged)) as types.IOrganizationChartNode | types.IOrganizationChartNode[];
  const mergedNodes = Array.isArray(stored) ? stored : [stored];
  return { nodes: mergedNodes, chart: result.chart };
}

export function* deleteNode(nodeId: types.IOrganizationChartNode['id']) {
  const result = (yield call(apiRequest, api.deleteNodeApi(nodeId))) as types.IChartNodeDeleteResult | null;
  if (!result) {
    return;
  }
  yield call(clearNode, nodeId);
  return { nodeId, chart: result.chart };
}
